import { useState, useEffect, useCallback } from 'react';
import { X, Download, Share, PlusSquare } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

// Detect iOS
const isIOS = () => {
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
};

// Detect if running in standalone (installed) mode
const isStandalone = () => {
  return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
};

const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showAndroidPrompt, setShowAndroidPrompt] = useState(false);
  const [showIOSPrompt, setShowIOSPrompt] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Don't show if already installed or dismissed
    if (isStandalone()) return;
    if (sessionStorage.getItem('pwa_dismissed')) return;

    // Android: capture beforeinstallprompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowAndroidPrompt(true);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // iOS: show guided prompt after a delay
    if (isIOS() && !window.navigator.standalone) {
      const timer = setTimeout(() => {
        setShowIOSPrompt(true);
      }, 3000);
      return () => {
        clearTimeout(timer);
        window.removeEventListener('beforeinstallprompt', handler);
      };
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleAndroidInstall = useCallback(async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setShowAndroidPrompt(false);
    }
    setDeferredPrompt(null);
  }, [deferredPrompt]);

  const handleDismiss = useCallback(() => {
    setShowAndroidPrompt(false);
    setShowIOSPrompt(false);
    setDismissed(true);
    sessionStorage.setItem('pwa_dismissed', 'true');
  }, []);

  if (dismissed || isStandalone()) return null;

  return (
    <AnimatePresence>
      {/* Android Install Prompt */}
      {showAndroidPrompt && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          className="fixed bottom-0 left-0 right-0 z-50 p-4 safe-area-bottom"
          data-testid="android-install-prompt"
        >
          <div className="bg-pt-surface border border-pt-border rounded-lg p-4 shadow-2xl max-w-md mx-auto">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <img src="/icon-192.png" alt="Gym Axis" className="w-10 h-10 rounded-lg" />
                <div>
                  <h3 className="text-white font-bold text-sm">Install Gym Axis</h3>
                  <p className="text-pt-muted text-xs">Get the full app experience</p>
                </div>
              </div>
              <button onClick={handleDismiss} className="text-pt-muted hover:text-white p-1">
                <X className="w-4 h-4" />
              </button>
            </div>
            <button
              data-testid="install-btn"
              onClick={handleAndroidInstall}
              className="w-full h-11 bg-pt-primary text-black font-bold text-sm uppercase tracking-wide rounded-sm hover:opacity-90 transition-all active:scale-[0.98] flex items-center justify-center gap-2"
            >
              <Download className="w-4 h-4" />
              Install Gym Axis
            </button>
          </div>
        </motion.div>
      )}

      {/* iOS Install Guide */}
      {showIOSPrompt && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          className="fixed bottom-0 left-0 right-0 z-50 p-4 safe-area-bottom"
          data-testid="ios-install-prompt"
        >
          <div className="bg-pt-surface border border-pt-border rounded-lg p-4 shadow-2xl max-w-md mx-auto">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <img src="/icon-192.png" alt="Gym Axis" className="w-10 h-10 rounded-lg" />
                <div>
                  <h3 className="text-white font-bold text-sm">Install Gym Axis</h3>
                  <p className="text-pt-muted text-xs">For a better experience</p>
                </div>
              </div>
              <button onClick={handleDismiss} className="text-pt-muted hover:text-white p-1">
                <X className="w-4 h-4" />
              </button>
            </div>
            
            <div className="space-y-3 mb-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                  <Share className="w-4 h-4 text-blue-400" />
                </div>
                <p className="text-pt-muted text-xs">
                  <span className="text-white font-medium">Step 1:</span> Tap the Share button in Safari
                </p>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-pt-primary/20 flex items-center justify-center flex-shrink-0">
                  <PlusSquare className="w-4 h-4 text-pt-primary" />
                </div>
                <p className="text-pt-muted text-xs">
                  <span className="text-white font-medium">Step 2:</span> Tap "Add to Home Screen"
                </p>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
                  <Download className="w-4 h-4 text-green-400" />
                </div>
                <p className="text-pt-muted text-xs">
                  <span className="text-white font-medium">Step 3:</span> Tap "Add" to install
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PWAInstallPrompt;
